import { useEffect, useState } from 'react';
import { BOT_SKILL_LABELS, FACTION_SPECS, SKILLS as BOT_SKILLS } from '@tb/shared';
import { CLUSTERS_PER_REGION, MATCH_SIZES, POCKET_MAX } from '@tb/shared';
import type { BotSkill, Faction } from '@tb/shared';
import type { Net } from './net';
import { GraphicsSetting } from './front/GraphicsSetting';
import { Icon, Notice, Segmented, Tab, Tabs, Tag } from './ui/kit';
import { rememberPassword, rememberedPassword } from './front/gate';
import { AdminDashboard } from './front/AdminDashboard';

export { FrontFrame, FrontName, FrontLobby, FrontDeployment, FrontLoading, startTraining } from './front/Lobby';

/** The simulation room remains a separate, password-protected address. */
export function onSimPath(): boolean {
  return location.pathname.replace(/\/+$/, '') === '/sim';
}

// --- the simulation room ------------------------------------------------------

const SKILLS = BOT_SKILLS.map((id) => ({ id, label: BOT_SKILL_LABELS[id] }));

/**
 * /sim, the admin centre: a password, then the dashboard (the switch that
 * opens the game to the public, and what has been played, D36) and the
 * NPC-against-NPC simulations to run and watch, with the labs. The password
 * is kept for this tab only, and the server checks it again on every call.
 */
export function SimRoom({ net, name }: { net: Net; name: string }) {
  const [password, setPassword] = useState(() => rememberedPassword());
  const [guess, setGuess] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);

  const unlock = async (e: React.FormEvent) => {
    e.preventDefault();
    setChecking(true);
    setProblem(null);
    try {
      const r = await fetch('/sim/unlock', {
        method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ password: guess }),
      });
      if (r.status === 204) {
        rememberPassword(guess);
        setPassword(guess);
      } else {
        setProblem(r.status === 429 ? 'Too many wrong guesses. Try again later.' : 'That is not the password.');
      }
    } catch {
      setProblem('Could not reach the server.');
    } finally {
      setChecking(false);
    }
  };

  if (!password) {
    return (
      <div className="centered terminal-room">
        <div className="card housing sim-gate">
          <div className="titleblock">
            <span className="eyebrow">Simulation room</span>
            <h1>Restricted</h1>
          </div>
          <p className="sub">The dashboard, NPC-against-NPC simulations and the labs. Enter the password to go in.</p>
          <form className="row" onSubmit={unlock} style={{ marginTop: 14 }}>
            <label className="sr-only" htmlFor="sim-password">Password</label>
            <input
              id="sim-password"
              className="grow"
              type="password"
              autoComplete="current-password"
              value={guess}
              onChange={(e) => setGuess(e.target.value)}
              autoFocus
            />
            <button className="btn primary" type="submit" disabled={checking || !guess}>Unlock</button>
          </form>
          {problem && <Notice tone="bad" inline className="sim-problem">{problem}</Notice>}
          <p className="sub" style={{ marginTop: 14 }}><a href="/"><Icon name="arrow-left" /> Back to the front</a></p>
        </div>
      </div>
    );
  }
  return <AdminRoom net={net} name={name} password={password} onLocked={() => setPassword(null)} />;
}

type RoomTab = 'dashboard' | 'simulations';
const ROOM_TAB_KEY = 'tb.sim.tab';

/** The unlocked room: the dashboard and the simulations, a tab each, the last one chosen kept for this tab. */
function AdminRoom({ net, name, password, onLocked }: { net: Net; name: string; password: string; onLocked: () => void }) {
  const [tab, setTabState] = useState<RoomTab>(() => {
    try { return sessionStorage.getItem(ROOM_TAB_KEY) === 'simulations' ? 'simulations' : 'dashboard'; } catch { return 'dashboard'; }
  });
  const setTab = (t: RoomTab) => {
    setTabState(t);
    try { sessionStorage.setItem(ROOM_TAB_KEY, t); } catch { /* the default next time */ }
  };
  return (
    <div className={`centered sim-room ${tab === 'dashboard' ? 'wide' : ''}`}>
      <div className="card housing">
        <div className="titleblock">
          <span className="eyebrow">Simulation room</span>
          <h1>Trading Blows</h1>
        </div>
        <p className="sub" style={{ marginBottom: 12 }}>
          {net.state === 'open' ? 'Connected' : 'Connecting…'}{name && <> as <strong>{name}</strong></>}
          {' · '}<a href="/">back to the front</a>
        </p>
        <Tabs label="Simulation room" className="sim-tabs">
          <Tab on={tab === 'dashboard'} onClick={() => setTab('dashboard')} data-testid="sim-tab-dashboard">Dashboard</Tab>
          <Tab on={tab === 'simulations'} onClick={() => setTab('simulations')} data-testid="sim-tab-simulations">Simulations</Tab>
        </Tabs>
        {tab === 'dashboard'
          ? <AdminDashboard password={password} onLocked={onLocked} />
          : <SimControls net={net} password={password} />}
        <GraphicsSetting />
      </div>
    </div>
  );
}

function SimControls({ net, password }: { net: Net; password: string }) {
  // A 16v16 at 30s shows you a war: long enough per attack phase to watch the
  // whole exchange come down. 'battle' is one 4v4 cluster without ICBMs.
  const [size, setSize] = useState(String(MATCH_SIZES[0]));
  const battle = size === 'battle';
  const [seconds, setSeconds] = useState(30);
  const [west, setWest] = useState<BotSkill>('VETERAN');
  const [east, setEast] = useState<BotSkill>('VETERAN');
  /** The side the watcher sits with: its cities, its books, its picture of the enemy. */
  const [watch, setWatch] = useState<Faction>('WEST');
  // The scenario lab answers only on the machine running the server.
  const [lab, setLab] = useState(false);
  useEffect(() => {
    fetch('/lab/api/status').then((r) => setLab(r.ok)).catch(() => setLab(false));
  }, []);
  const sims = net.matches.filter((m) => m.sim && m.status === 'RUNNING');

  return (
    <>
        {sims.length > 0 && <div className="section-label">Running</div>}
        {sims.map((m) => (
          <div key={m.id} className="item">
            <div className="item-title">
              <strong className="mono">{m.id}</strong>
              <Tag tone="ok">round {m.round}</Tag>
            </div>
            <div className="meta">{m.perSide}v{m.perSide}{m.perSide === POCKET_MAX && ' battle'}{m.spectators > 0 && ` · ${m.spectators} watching`}</div>
            <div className="row watch-sides">
              <button className="btn sm grow faction-WEST" onClick={() => net.spectate(m.id, 'WEST')}>Watch the Frontier</button>
              <button className="btn sm grow faction-EAST" onClick={() => net.spectate(m.id, 'EAST')}>Watch the Heritage</button>
            </div>
          </div>
        ))}

        <div className="section-label">New simulation</div>
        <p className="sub" style={{ fontSize: 'var(--fs-sm)', marginBottom: 8 }}>
          NPC against NPC with nobody playing. You watch one side: any of its cities up close, and the
          whole side from above — its cities, books and supply, and what it knows of the enemy. Lift the
          fog to set that against the truth.
        </p>
        <div className="stack">
          <label className="row wrap">
            <span className="grow sub">Cities per side</span>
            <select value={size} onChange={(e) => setSize(e.target.value)} style={{ width: 210 }}>
              <option value="battle">{POCKET_MAX}v{POCKET_MAX} battle · one cluster, no ICBMs</option>
              {MATCH_SIZES.map((n) => (
                <option key={n} value={n}>{n}v{n} · {n / (CLUSTERS_PER_REGION * POCKET_MAX)} region{n === CLUSTERS_PER_REGION * POCKET_MAX ? '' : 's'}, {n / POCKET_MAX} clusters</option>
              ))}
            </select>
          </label>
          <label className="row wrap">
            <span className="grow sub">Seconds per phase</span>
            <select value={seconds} onChange={(e) => setSeconds(Number(e.target.value))} style={{ width: 110 }}>
              {[15, 20, 30, 45, 60, 90].map((n) => <option key={n} value={n}>{n}s</option>)}
            </select>
          </label>
          <label className="row wrap">
            <span className="grow sub faction-WEST">{FACTION_SPECS.WEST.title}</span>
            <select value={west} onChange={(e) => setWest(e.target.value as BotSkill)} style={{ width: 210, maxWidth: '100%' }}>
              {SKILLS.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
            </select>
          </label>
          <label className="row wrap">
            <span className="grow sub faction-EAST">{FACTION_SPECS.EAST.title}</span>
            <select value={east} onChange={(e) => setEast(e.target.value as BotSkill)} style={{ width: 210, maxWidth: '100%' }}>
              {SKILLS.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
            </select>
          </label>
          <div className="row">
            <span className="grow sub">Watch</span>
            <Segmented label="Side to watch" value={watch} onChange={setWatch}
              options={(['WEST', 'EAST'] as const).map((f) => ({ value: f, label: <span className={`faction-${f}`}>{FACTION_SPECS[f].title}</span> }))} />
          </div>
          <button className="btn primary" onClick={() => net.createSim(battle ? POCKET_MAX : Number(size), seconds, west, east, watch, password, battle)}>
            Run simulation
          </button>
          <a className="btn ghost" href="/sim/lab/">Headless tuning lab: concurrent matches and telemetry</a>
          <a className="btn ghost" href="/sim/lab/exchanges.html" data-testid="sim-exchange-bench">Exchange bench: salvos against air-defence belts, side by side</a>
          {lab && <a className="btn ghost" href="/lab.html">Scenario lab: headless experiments and tuning</a>}
        </div>
    </>
  );
}
